import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
const { FiSearch, FiMapPin, FiCalendar, FiMessageCircle, FiPhone, FiX, FiCheck, FiList, FiMap, FiBriefcase, FiStar, FiClock, FiBook, FiChevronRight } = FiIcons;
import SafeIcon from '../../common/SafeIcon';
import { writeFetch, getSession } from '../../api';

const PROVIDERS_TABLE = 'service_providers_1777741098';
const LISTINGS_TABLE = 'merchant_listings_1777866100042';
const BOOKINGS_TABLE = 'service_bookings_1777866100042';

const CATEGORIES = ['ALL', 'FUNERAL HOME', 'CREMATION SERVICE', 'MEMORIAL CHAPEL', 'GRIEF COUNSELOR', 'CEMETERY'];

const MOCK_PROVIDERS = [
  {
    id: 'p1', owner_id: 'owner1', name: 'Serenity Funeral Home', type: 'Funeral Home',
    description: 'A family-owned funeral home serving the community with compassion for over 60 years. We offer dignified, personalized services tailored to every family.',
    address: '142 Peaceful Lane, New York, NY 10001', phone: '+1 (212) 555-0191',
    whatsapp: '+12125550191', whatsapp_enabled: true,
    image_url: 'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?q=80&w=200&auto=format&fit=crop',
    rating: 4.9,
    refund_policy: 'No refunds after 24 hours of booking.',
    cancellation_policy: 'Cancellations must be made 48 hours in advance to receive a full refund.',
    tagline: 'Compassionate care in every detail.'
  },
  {
    id: 'p2', owner_id: 'owner2', name: 'Tranquil Gardens Cremation', type: 'Cremation Service',
    description: 'Eco-friendly and respectful cremation services with beautiful scattering gardens.',
    address: '88 Willow Drive, Brooklyn, NY 11201', phone: '+1 (718) 555-8822',
    whatsapp_enabled: false,
    image_url: 'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?q=80&w=200&auto=format&fit=crop',
    rating: 4.7
  },
  {
    id: 'p3', owner_id: 'owner3', name: 'Healing Hearts Grief Counseling', type: 'Grief Counselor',
    description: 'Professional bereavement support and counseling for individuals and families navigating loss.',
    address: 'Online / Telehealth', phone: '+1 (800) 555-HEAL',
    whatsapp: '+18005554325', whatsapp_enabled: true,
    image_url: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?q=80&w=200&auto=format&fit=crop',
    rating: 4.95
  }
];

const MOCK_SERVICES = [
  {
    id: 's1', owner_id: 'owner1', title: 'Traditional Funeral Package', description: 'Complete service including viewing, ceremony, and transportation.',
    price: 4500, category: 'Funeral Planning', duration_minutes: 180, service_type: 'bookable', pricing_model: 'paid'
  },
  {
    id: 's2', owner_id: 'owner1', title: 'Memorial Consultation', description: 'Initial planning meeting to discuss options and arrangements.',
    price: 0, category: 'Funeral Planning', duration_minutes: 60, service_type: 'bookable', pricing_model: 'free'
  },
  {
    id: 's4', owner_id: 'owner1', title: 'Chapel Reservation', description: 'Reserve the main chapel for a memorial service.',
    price: 500, category: 'Venue', duration_minutes: 120, service_type: 'reservation', pricing_model: 'paid'
  },
  {
    id: 's3', owner_id: 'owner3', title: 'Individual Counseling Session', description: 'One-on-one grief counseling session with a licensed therapist.',
    price: 120, category: 'Grief Counseling', duration_minutes: 60, service_type: 'bookable', pricing_model: 'paid'
  }
];

export default function LocalProvidersModal({ isOpen, onClose }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('ALL');
  const [viewMode, setViewMode] = useState('list');
  
  const [providers, setProviders] = useState([]);
  const [services, setServices] = useState([]);
  const [selectedProvider, setSelectedProvider] = useState(null);
  
  const [loading, setLoading] = useState(true);
  
  // Wizard State
  const [bookingProvider, setBookingProvider] = useState(null);
  const [bookingStep, setBookingStep] = useState('datetime');
  const [bookingDate, setBookingDate] = useState('');
  const [bookingTime, setBookingTime] = useState('');
  const [customerDetails, setCustomerDetails] = useState({ name: '', email: '', phone: '', notes: '' });
  const [isProcessing, setIsProcessing] = useState(false);
  const [bookingSuccess, setBookingSuccess] = useState(false);

  const fetchProvidersAndServices = useCallback(async () => {
    setLoading(true);
    let loadedProviders = [];
    let loadedServices = [];

    try {
      const [provRes, servRes] = await Promise.allSettled([
        writeFetch(`read/${PROVIDERS_TABLE}?limit=100`),
        writeFetch(`read/${LISTINGS_TABLE}?limit=100`)
      ]);

      if (provRes.status === 'fulfilled') {
        const provData = await provRes.value.json();
        if (provData.status === 'success' && provData.data?.length > 0) {
          loadedProviders = provData.data;
        }
      }

      if (servRes.status === 'fulfilled') {
        const servData = await servRes.value.json();
        if (servData.status === 'success' && servData.data?.length > 0) {
          loadedServices = servData.data;
        }
      }
    } catch (error) {
      console.error('Error fetching directory:', error);
    }

    MOCK_PROVIDERS.forEach(mp => {
      if (!loadedProviders.find(p => p.id === mp.id)) loadedProviders.push(mp);
    });
    
    for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('mock_provider_')) {
           try {
              const ownerIdFromKey = key.replace('mock_provider_', '');
              const p = JSON.parse(localStorage.getItem(key));
              if (!p.owner_id) p.owner_id = ownerIdFromKey;
              if (!p.id) p.id = `mock_${ownerIdFromKey}`;
              
              if (!loadedProviders.find(x => x.id === p.id)) loadedProviders.push(p);
           } catch(e) {}
        }
    }

    MOCK_SERVICES.forEach(ms => {
       if (!loadedServices.find(s => s.id === ms.id)) loadedServices.push(ms);
    });

    for (let i = 0; i < localStorage.length; i++) {
       const key = localStorage.key(i);
       if (key && key.startsWith('mock_listings_')) {
          try {
             const ownerIdFromKey = key.replace('mock_listings_', '');
             const arr = JSON.parse(localStorage.getItem(key));
             if (Array.isArray(arr)) {
                arr.forEach(s => {
                   if (!s.owner_id) s.owner_id = ownerIdFromKey;
                   if (!loadedServices.find(x => x.id === s.id)) loadedServices.push(s);
                });
             }
          } catch(e) {}
       }
    }

    setProviders(loadedProviders);
    setServices(loadedServices);
    if (loadedProviders.length > 0) {
        setSelectedProvider(loadedProviders[0]);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (isOpen) {
        fetchProvidersAndServices();
    }
  }, [isOpen, fetchProvidersAndServices]);

  const filteredProviders = useMemo(() => {
    return providers.filter(p => {
      const matchesSearch = p.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                            p.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                            p.address?.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesCategory = selectedCategory === 'ALL' || 
                              p.type?.toUpperCase() === selectedCategory;
      return matchesSearch && matchesCategory;
    });
  }, [providers, searchQuery, selectedCategory]);

  useEffect(() => {
    if (filteredProviders.length > 0 && (!selectedProvider || !filteredProviders.find(p => p.id === selectedProvider.id))) {
      setSelectedProvider(filteredProviders[0]);
    } else if (filteredProviders.length === 0) {
      setSelectedProvider(null);
    }
  }, [filteredProviders, selectedProvider]);

  const providerServices = useMemo(() => {
    if (!selectedProvider) return [];
    return services.filter(s => String(s.owner_id) === String(selectedProvider.owner_id));
  }, [services, selectedProvider]);

  const appointments = useMemo(() => providerServices.filter(s => s.service_type === 'bookable' || !s.service_type), [providerServices]);
  const reservations = useMemo(() => providerServices.filter(s => s.service_type === 'reservation'), [providerServices]);

  const openBookingWizard = (serviceOrProvider) => {
    setBookingProvider(serviceOrProvider);
    setBookingStep('datetime');
    
    // Set default date to tomorrow
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    setBookingDate(tomorrow.toISOString().split('T')[0]);
    
    setBookingTime('');
    setBookingSuccess(false);

    let user = getSession();
    setCustomerDetails({
      name: user?.name || '',
      email: user?.email || '',
      phone: '',
      notes: ''
    });
  };

  const handleConfirmBooking = async () => {
    setIsProcessing(true);
    let user = getSession();
    if (!user) {
      user = { id: 'guest_' + Math.random().toString(36).substr(2, 9), name: customerDetails.name, email: customerDetails.email };
    }

    const isGeneral = bookingProvider.id === 'general';
    const merchantId = isGeneral ? bookingProvider.owner_id : (bookingProvider.owner_id || selectedProvider.owner_id);

    const adminAutoApprove = localStorage.getItem('admin_auto_approve_bookings') === 'true';
    const merchantAutoApprove = localStorage.getItem('merchant_auto_approve_bookings') === 'true';
    
    let initialStatus = 'pending_admin';
    
    // Core Logic differentiating Reservation vs Booking
    if (bookingProvider.service_type === 'reservation') {
      // Reservations always require manual approval, no auto-approval allowed
      initialStatus = bookingProvider.pricing_model === 'free' ? 'pending_merchant' : 'pending_admin';
    } else {
      // Bookings can be auto-approved based on merchant settings
      if (bookingProvider.pricing_model === 'free') {
        if (adminAutoApprove && merchantAutoApprove) initialStatus = 'confirmed';
        else if (adminAutoApprove && !merchantAutoApprove) initialStatus = 'pending_merchant';
      }
    }

    const bookingData = {
      id: crypto.randomUUID(),
      listing_id: isGeneral ? 'general' : bookingProvider.id,
      listing_title: isGeneral ? 'General Booking Request' : bookingProvider.title,
      merchant_id: merchantId,
      customer_id: user.id,
      customer_name: customerDetails.name,
      customer_email: customerDetails.email,
      customer_phone: customerDetails.phone,
      start_time: `${bookingDate}T${bookingTime}`,
      notes: customerDetails.notes,
      price: bookingProvider.price || 0,
      status: initialStatus,
      created_at: new Date().toISOString(),
      owner_id: user.id
    };

    try {
      await writeFetch(`create/${BOOKINGS_TABLE}`, {
        method: 'POST',
        body: JSON.stringify(bookingData)
      });
    } catch (error) {
      console.error('Booking API error:', error);
    }

    // Fallback logic to ensure it shows up in Merchant Portal if API fails
    const local = JSON.parse(localStorage.getItem(`mock_bookings_${merchantId}`) || '[]');
    local.push(bookingData);
    localStorage.setItem(`mock_bookings_${merchantId}`, JSON.stringify(local));

    setIsProcessing(false);
    setBookingSuccess(true);
    setBookingStep('success');

    setTimeout(() => {
      setBookingSuccess(false);
      setBookingProvider(null);
    }, 3000);
  };

  const openWhatsApp = (phone) => {
    if (!phone) return;
    const cleanPhone = phone.replace(/\D/g, '');
    window.open(`https://wa.me/${cleanPhone}`, '_blank');
  };

  const renderServiceCard = (s) => (
    <div key={s.id} className="p-5 border border-gray-200 rounded-2xl flex flex-col md:flex-row justify-between items-start md:items-center gap-4 hover:border-[#c8a97e] transition-colors bg-white shadow-sm group">
      <div className="flex-1">
        <div className="flex items-center gap-2 mb-1">
          <h4 className="font-bold text-[#1e293b] text-base group-hover:text-[#c8a97e] transition-colors">{s.title}</h4>
          {s.pricing_model === 'free' && (
            <span className="px-2 py-0.5 bg-green-100 text-green-700 text-[9px] font-black uppercase tracking-widest rounded-md">Free</span>
          )}
        </div>
        <p className="text-sm text-gray-500 mt-1 line-clamp-2">{s.description}</p>
        <div className="text-[10px] font-black text-gray-400 mt-3 uppercase tracking-widest flex items-center gap-2">
          <span className="flex items-center gap-1"><SafeIcon icon={FiClock} className="w-3 h-3 mb-0.5" /> {s.duration_minutes} MINS</span>
          <span>•</span>
          <span className={s.pricing_model === 'paid' ? 'text-[#1e293b]' : 'text-green-600'}>
            {s.pricing_model === 'free' ? 'FREE' : `$${parseFloat(s.price || 0).toFixed(2)}`}
          </span>
        </div>
      </div>
      <button 
        onClick={() => { openBookingWizard(s); }}
        className="w-full md:w-auto px-6 py-3 bg-[#1e293b] text-white text-[10px] font-black uppercase tracking-widest rounded-xl hover:bg-[#2a3b52] transition-colors shadow-md shrink-0 flex items-center justify-center gap-2"
      >
        {s.service_type === 'reservation' ? 'Request Reservation' : 'Book Appointment'}
      </button>
    </div>
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        className="bg-white w-full max-w-6xl h-[90vh] rounded-3xl shadow-2xl overflow-hidden flex flex-col"
      >
        {/* Header */}
        <div className="p-6 border-b border-gray-100 flex items-center justify-between shrink-0 bg-white z-10 relative">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-[#c8a97e] rounded-2xl flex items-center justify-center shadow-md">
              <SafeIcon icon={FiMapPin} className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-serif text-[#1e293b]">Local Service Providers</h2>
              <p className="text-[10px] font-black tracking-widest uppercase text-gray-400 mt-1">
                Funeral Homes · Chapels · Grief Counselors
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-400">
            <SafeIcon icon={FiX} className="w-6 h-6" />
          </button>
        </div>

        {/* Search & Filters */}
        <div className="p-4 border-b border-gray-100 shrink-0 bg-white z-10 relative">
          <div className="flex gap-4 mb-4">
            <div className="relative flex-1">
              <SafeIcon icon={FiSearch} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name, type, or location..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#c8a97e] focus:ring-1 focus:ring-[#c8a97e] transition-all"
              />
            </div>
            <div className="flex bg-gray-50 border border-gray-200 rounded-xl p-1 shrink-0">
              <button 
                onClick={() => setViewMode('list')}
                className={`p-2 rounded-lg transition-colors ${viewMode === 'list' ? 'bg-[#1e293b] text-white shadow-sm' : 'text-gray-400 hover:text-gray-600'}`}>
                <SafeIcon icon={FiList} className="w-5 h-5" />
              </button>
              <button 
                onClick={() => setViewMode('map')}
                className={`p-2 rounded-lg transition-colors ${viewMode === 'map' ? 'bg-[#1e293b] text-white shadow-sm' : 'text-gray-400 hover:text-gray-600'}`}>
                <SafeIcon icon={FiMap} className="w-5 h-5" />
              </button>
            </div>
          </div>
          <div className="flex gap-2 overflow-x-auto pb-1 custom-scrollbar">
            {CATEGORIES.map(cat => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                className={`px-4 py-2 rounded-full text-xs font-bold whitespace-nowrap transition-all border ${
                  selectedCategory === cat
                    ? 'bg-[#1e293b] text-white border-[#1e293b]'
                    : 'bg-white text-gray-500 border-gray-200 hover:border-gray-300'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        {/* Main Content Area */}
        {loading ? (
           <div className="flex-1 flex flex-col items-center justify-center bg-gray-50/50">
             <div className="w-12 h-12 border-4 border-[#c8a97e] border-t-transparent rounded-full animate-spin mb-4"></div>
             <p className="text-gray-400 font-medium">Finding providers in your area...</p>
           </div>
        ) : filteredProviders.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center p-8 text-center bg-gray-50/50">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <SafeIcon icon={FiSearch} className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-xl font-bold text-[#1e293b] mb-2">No providers found</h3>
            <p className="text-gray-500 text-sm max-w-xs">We couldn't find any providers matching your search criteria. Try adjusting your filters.</p>
          </div>
        ) : viewMode === 'list' ? (
          <div className="flex-1 flex overflow-hidden">
            
            {/* Left Sidebar: Provider List */}
            <div className="w-1/3 min-w-[320px] max-w-[420px] border-r border-gray-100 bg-gray-50/30 overflow-y-auto p-4 space-y-4 custom-scrollbar">
              {filteredProviders.map(p => (
                 <div key={p.id} 
                      className={`p-4 rounded-2xl border-2 cursor-pointer transition-all flex flex-col bg-white ${
                        selectedProvider?.id === p.id 
                        ? 'border-[#c8a97e] shadow-md' 
                        : 'border-gray-100 hover:border-gray-200 shadow-sm'
                      }`}
                      onClick={() => setSelectedProvider(p)}
                 >
                   <div className="flex gap-4">
                     <img src={p.image_url || 'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?q=80&w=200&auto=format&fit=crop'} 
                          alt={p.name} 
                          className="w-16 h-16 rounded-2xl object-cover shrink-0" 
                     />
                     <div className="flex-1 min-w-0">
                       <div className="flex justify-between items-start gap-2">
                         <h4 className="font-serif text-lg text-[#1e293b] leading-tight truncate">{p.name}</h4>
                         {selectedProvider?.id === p.id && (
                           <SafeIcon icon={FiCheck} className="text-green-500 w-5 h-5 shrink-0" />
                         )}
                       </div>
                       <div className="inline-block px-2 py-0.5 mt-1.5 bg-[#f5f0e6] text-[#8b6f4e] text-[9px] font-black tracking-widest uppercase rounded">
                         {p.type}
                       </div>
                       <div className="flex items-center gap-1 mt-2 text-sm font-bold text-gray-600">
                         <div className="flex text-[#c8a97e] text-xs">
                           <SafeIcon icon={FiStar} className="fill-current" />
                           <SafeIcon icon={FiStar} className="fill-current" />
                           <SafeIcon icon={FiStar} className="fill-current" />
                           <SafeIcon icon={FiStar} className="fill-current" />
                           <SafeIcon icon={FiStar} className="fill-current" />
                         </div>
                         <span className="ml-1 text-xs">{p.rating || '4.9'}</span>
                       </div>
                     </div>
                   </div>
                 </div>
              ))}
            </div>
            
            {/* Right Area: Provider Details & Services */}
            {selectedProvider ? (
              <div className="flex-1 overflow-y-auto bg-white flex flex-col relative custom-scrollbar">
                <div className="p-8">
                  {/* Dynamic Map Component */}
                  <div className="w-full h-48 min-h-[192px] shrink-0 rounded-3xl bg-gray-200 overflow-hidden mb-6 relative border border-gray-100 shadow-sm">
                    <iframe
                      width="100%"
                      height="100%"
                      frameBorder="0"
                      style={{ border: 0 }}
                      src={`https://maps.google.com/maps?q=${encodeURIComponent(selectedProvider.address || selectedProvider.name)}&t=&z=14&ie=UTF8&iwloc=&output=embed`}
                      allowFullScreen
                      title="Provider Location"
                    ></iframe>
                    <div className="absolute bottom-3 inset-x-0 flex justify-center pointer-events-none">
                      <div className="bg-white/95 backdrop-blur-sm px-4 py-2 rounded-full shadow-lg font-bold text-[#1e293b] flex items-center gap-2 border border-gray-100 max-w-[90%] pointer-events-auto">
                          <SafeIcon icon={FiMapPin} className="text-red-500 w-4 h-4 shrink-0" />
                          <span className="text-xs truncate">{selectedProvider.address || 'Location Details'}</span>
                      </div>
                    </div>
                  </div>

                  {/* Provider Header */}
                  <h2 className="text-xl md:text-2xl font-serif text-[#1e293b] mb-2 leading-tight mt-4">{selectedProvider.name}</h2>
                  <div className="inline-block px-3 py-1 bg-[#f5f0e6] text-[#8b6f4e] text-[10px] font-black tracking-widest uppercase rounded mb-6">
                    {selectedProvider.type}
                  </div>

                  {/* Evenly Split Contact Call Buttons */}
                  <div className="flex flex-row gap-3 mb-6">
                    {selectedProvider.whatsapp_enabled && (
                      <button 
                        onClick={() => openWhatsApp(selectedProvider.whatsapp || selectedProvider.phone)}
                        className="flex-1 flex items-center justify-center gap-2 py-3 bg-[#22c55e] text-white rounded-xl text-[10px] font-black tracking-widest uppercase hover:bg-[#16a34a] transition-colors shadow-sm"
                      >
                        <SafeIcon icon={FiMessageCircle} className="w-4 h-4" /> WhatsApp
                      </button>
                    )}
                    <button 
                      onClick={() => { if(selectedProvider.phone) window.location.href = `tel:${selectedProvider.phone}`; }}
                      className="flex-1 flex items-center justify-center gap-2 py-3 bg-[#1e293b] text-white rounded-xl text-[10px] font-black tracking-widest uppercase hover:bg-[#0f172a] transition-colors shadow-sm"
                    >
                      <SafeIcon icon={FiPhone} className="w-4 h-4" /> Call
                    </button>
                  </div>

                  {/* Description Excerpt */}
                  {selectedProvider.tagline && (
                    <p className="text-lg font-medium text-[#c8a97e] mb-4">{selectedProvider.tagline}</p>
                  )}

                  <p className="text-gray-600 leading-relaxed mb-6 text-sm md:text-base line-clamp-4">
                    {selectedProvider.description}
                  </p>

                  {/* Address & Phone Container */}
                  <div className="space-y-4 mb-8 p-5 bg-gray-50 rounded-2xl border border-gray-100">
                    <div className="flex items-center gap-3 text-gray-700">
                      <SafeIcon icon={FiMapPin} className="w-5 h-5 text-[#c8a97e] shrink-0" />
                      <span className="font-medium text-sm md:text-base">{selectedProvider.address || 'Address not listed'}</span>
                    </div>
                    {selectedProvider.phone && (
                      <div className="flex items-center gap-3 text-gray-700">
                        <SafeIcon icon={FiPhone} className="w-5 h-5 text-[#c8a97e] shrink-0" />
                        <span className="font-medium text-sm md:text-base">{selectedProvider.phone}</span>
                      </div>
                    )}
                  </div>

                  {/* Store Policies Section */}
                  {(selectedProvider.refund_policy || selectedProvider.cancellation_policy || selectedProvider.delivery_policy) && (
                    <div className="mb-8 p-5 bg-gray-50 rounded-2xl border border-gray-100">
                      <h3 className="text-[10px] font-black text-[#1e293b] tracking-widest uppercase mb-4">Store Policies</h3>
                      <div className="space-y-4">
                        {selectedProvider.refund_policy && (
                          <div>
                            <h4 className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Refund Policy</h4>
                            <p className="text-sm text-gray-600">{selectedProvider.refund_policy}</p>
                          </div>
                        )}
                        {selectedProvider.cancellation_policy && (
                          <div>
                            <h4 className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Cancellation Policy</h4>
                            <p className="text-sm text-gray-600">{selectedProvider.cancellation_policy}</p>
                          </div>
                        )}
                        {selectedProvider.delivery_policy && (
                          <div>
                            <h4 className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Delivery Policy</h4>
                            <p className="text-sm text-gray-600">{selectedProvider.delivery_policy}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Sync'd Services Section */}
                  <div className="space-y-8 pt-6 border-t border-gray-100">
                    {appointments.length > 0 && (
                      <div>
                        <h3 className="text-[10px] font-black text-[#1e293b] tracking-widest uppercase mb-4 flex items-center gap-2">
                          <SafeIcon icon={FiCalendar} /> Appointments
                        </h3>
                        <div className="space-y-3">
                          {appointments.map(renderServiceCard)}
                        </div>
                      </div>
                    )}

                    {reservations.length > 0 && (
                      <div>
                        <h3 className="text-[10px] font-black text-[#1e293b] tracking-widest uppercase mb-4 flex items-center gap-2">
                          <SafeIcon icon={FiBook} /> General Availability / Reservations
                        </h3>
                        <div className="space-y-3">
                          {reservations.map(renderServiceCard)}
                        </div>
                      </div>
                    )}

                    {appointments.length === 0 && reservations.length === 0 && (
                      <div className="p-10 border-2 border-dashed border-gray-200 rounded-3xl flex flex-col items-center text-center bg-gray-50/50">
                        <div className="w-16 h-16 bg-white rounded-2xl shadow-sm border border-gray-100 flex items-center justify-center mb-4">
                          <SafeIcon icon={FiBriefcase} className="w-8 h-8 text-gray-400" />
                        </div>
                        <p className="text-gray-500 mb-6 font-medium">No specific services listed.</p>
                        <button 
                          onClick={() => { openBookingWizard({ title: 'General Booking', id: 'general', owner_id: selectedProvider.owner_id }); }}
                          className="px-8 py-3.5 bg-[#c8a97e] text-white text-[10px] font-black uppercase tracking-widest rounded-xl hover:bg-[#b69a71] transition-colors shadow-lg"
                        >
                          Request General Booking
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : (
               <div className="flex-1 bg-white flex items-center justify-center">
                 <p className="text-gray-400">Select a provider to view details</p>
               </div>
            )}
          </div>
        ) : (
          <div className="flex-1 flex flex-col relative overflow-hidden bg-gray-100">
            <iframe
              width="100%"
              height="100%"
              frameBorder="0"
              style={{ border: 0 }}
              src={`https://maps.google.com/maps?q=${encodeURIComponent(selectedProvider?.address || selectedProvider?.name || 'New York')}&t=&z=14&ie=UTF8&iwloc=&output=embed`}
              allowFullScreen
              title="Area Map"
            ></iframe>
            
            <div className="absolute top-6 left-1/2 -translate-x-1/2 bg-white/95 backdrop-blur-md px-6 py-3 rounded-full shadow-lg font-bold text-[#1e293b] border border-gray-100 z-10 flex items-center gap-2">
              <SafeIcon icon={FiMapPin} className="text-[#c8a97e]" /> Showing {filteredProviders.length} Providers
            </div>
        
            <div className="absolute bottom-6 left-0 right-0 px-6 flex gap-4 overflow-x-auto pb-4 custom-scrollbar snap-x items-end z-10">
              {filteredProviders.map(p => (
                 <div key={p.id} 
                      onClick={() => setSelectedProvider(p)}
                      className={`snap-center shrink-0 w-80 p-5 rounded-3xl border-2 cursor-pointer transition-all flex flex-col bg-white shadow-xl ${
                        selectedProvider?.id === p.id 
                        ? 'border-[#c8a97e] scale-100 ring-4 ring-[#c8a97e]/20' 
                        : 'border-transparent scale-95 opacity-90 hover:opacity-100 hover:scale-100'
                      }`}
                 >
                   <div className="flex gap-4">
                     <img src={p.image_url || 'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?q=80&w=200&auto=format&fit=crop'} 
                          alt={p.name} 
                          className="w-16 h-16 rounded-2xl object-cover shrink-0" 
                     />
                     <div className="flex-1 min-w-0">
                       <h4 className="font-serif text-lg text-[#1e293b] leading-tight truncate">{p.name}</h4>
                       <div className="inline-block px-2 py-0.5 mt-1 bg-[#f5f0e6] text-[#8b6f4e] text-[9px] font-black tracking-widest uppercase rounded">
                         {p.type}
                       </div>
                       <div className="flex items-center gap-1 mt-2 text-sm font-bold text-gray-600">
                         <SafeIcon icon={FiStar} className="text-[#c8a97e] fill-current w-3 h-3" />
                         <span className="text-xs">{p.rating || '4.9'}</span>
                       </div>
                     </div>
                   </div>
                   {selectedProvider?.id === p.id && (
                     <button 
                       onClick={(e) => { e.stopPropagation(); setViewMode('list'); }}
                       className="mt-4 w-full py-2.5 bg-[#1e293b] text-white text-[10px] font-black uppercase tracking-widest rounded-xl hover:bg-[#0f172a] transition-colors flex items-center justify-center gap-2"
                     >
                       View Details & Book <SafeIcon icon={FiChevronRight} className="w-3 h-3" />
                     </button>
                   )}
                 </div>
              ))}
            </div>
          </div>
        )}
      </motion.div>

      {/* Booking Confirmation / Multi-Step Wizard Modal */}
      <AnimatePresence>
        {bookingProvider && (
          <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="bg-white w-full max-w-2xl p-0 rounded-[32px] shadow-2xl overflow-hidden flex flex-col"
            >
              <div className="bg-[#1e293b] p-6 text-white flex justify-between items-center">
                <div>
                  <h3 className="text-xl font-serif">
                    {bookingProvider.service_type === 'reservation' ? 'Request Reservation' : 'Book Appointment'}
                  </h3>
                  <p className="text-xs text-gray-300 mt-1">{bookingProvider.title} with {selectedProvider?.name}</p>
                </div>
                <button onClick={() => setBookingProvider(null)} className="p-2 hover:bg-white/10 rounded-full transition-colors">
                  <SafeIcon icon={FiX} className="w-6 h-6" />
                </button>
              </div>

              <div className="p-8">
                {/* Wizard Steps indicator */}
                {!bookingSuccess && (
                  <div className="flex justify-center mb-8">
                    <div className="flex items-center gap-2">
                       <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${bookingStep === 'datetime' ? 'bg-[#c8a97e] text-white' : 'bg-gray-200 text-gray-500'}`}>1</div>
                       <div className="w-8 h-1 bg-gray-200">
                          <div className={`h-full bg-[#c8a97e] transition-all ${bookingStep !== 'datetime' ? 'w-full' : 'w-0'}`} />
                       </div>
                       <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${bookingStep === 'details' ? 'bg-[#c8a97e] text-white' : (bookingStep === 'payment' || bookingStep === 'success' ? 'bg-[#c8a97e] text-white' : 'bg-gray-200 text-gray-500')}`}>2</div>
                       {bookingProvider.pricing_model === 'paid' && (
                         <>
                           <div className="w-8 h-1 bg-gray-200">
                              <div className={`h-full bg-[#c8a97e] transition-all ${bookingStep === 'payment' || bookingStep === 'success' ? 'w-full' : 'w-0'}`} />
                           </div>
                           <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${bookingStep === 'payment' ? 'bg-[#c8a97e] text-white' : 'bg-gray-200 text-gray-500'}`}>3</div>
                         </>
                       )}
                    </div>
                  </div>
                )}

                {/* Step 1: DateTime Selection */}
                {bookingStep === 'datetime' && (
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-bold text-[#1e293b] mb-2">Select Date</label>
                      <input 
                        type="date" 
                        min={new Date().toISOString().split('T')[0]}
                        value={bookingDate} 
                        onChange={e => setBookingDate(e.target.value)}
                        className="w-full p-4 bg-gray-50 border border-gray-200 rounded-xl focus:border-[#c8a97e] focus:ring-1 focus:ring-[#c8a97e] outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-bold text-[#1e293b] mb-2">Select Time</label>
                      <div className="grid grid-cols-3 gap-3">
                        {['09:00 AM', '10:00 AM', '11:00 AM', '01:00 PM', '02:00 PM', '03:00 PM', '04:00 PM'].map(time => (
                          <button
                            key={time}
                            onClick={() => setBookingTime(time)}
                            className={`p-3 rounded-xl text-sm font-bold border transition-all ${bookingTime === time ? 'bg-[#1e293b] text-white border-[#1e293b]' : 'bg-white text-gray-600 border-gray-200 hover:border-[#c8a97e]'}`}
                          >
                            {time}
                          </button>
                        ))}
                      </div>
                    </div>
                    <button
                      disabled={!bookingDate || !bookingTime}
                      onClick={() => setBookingStep('details')}
                      className="w-full py-4 bg-[#c8a97e] text-white rounded-xl font-black uppercase tracking-widest hover:bg-[#b69a71] transition-all disabled:opacity-50 disabled:cursor-not-allowed mt-4"
                    >
                      Next Step
                    </button>
                  </div>
                )}

                {/* Step 2: Customer Details */}
                {bookingStep === 'details' && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-1">Full Name</label>
                        <input type="text" value={customerDetails.name} onChange={e => setCustomerDetails(p => ({...p, name: e.target.value}))} className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl focus:border-[#c8a97e] outline-none" />
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-1">Email</label>
                        <input type="email" value={customerDetails.email} onChange={e => setCustomerDetails(p => ({...p, email: e.target.value}))} className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl focus:border-[#c8a97e] outline-none" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-1">Phone Number</label>
                      <input type="tel" value={customerDetails.phone} onChange={e => setCustomerDetails(p => ({...p, phone: e.target.value}))} className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl focus:border-[#c8a97e] outline-none" />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-1">Special Requirements / Notes</label>
                      <textarea value={customerDetails.notes} onChange={e => setCustomerDetails(p => ({...p, notes: e.target.value}))} rows="3" className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl focus:border-[#c8a97e] outline-none resize-none"></textarea>
                    </div>
                    
                    <div className="flex gap-4 pt-4">
                      <button onClick={() => setBookingStep('datetime')} className="w-1/3 py-4 bg-gray-100 text-gray-600 rounded-xl font-black uppercase tracking-widest hover:bg-gray-200 transition-all">Back</button>
                      <button 
                        disabled={!customerDetails.name || !customerDetails.email}
                        onClick={() => {
                          if (bookingProvider.pricing_model === 'paid' && bookingProvider.price > 0) {
                            setBookingStep('payment');
                          } else {
                            handleConfirmBooking();
                          }
                        }}
                        className="w-2/3 py-4 bg-[#c8a97e] text-white rounded-xl font-black uppercase tracking-widest hover:bg-[#b69a71] transition-all disabled:opacity-50"
                      >
                        {bookingProvider.pricing_model === 'paid' && bookingProvider.price > 0 ? 'Proceed to Payment' : (bookingProvider.service_type === 'reservation' ? 'Confirm Reservation Request' : 'Confirm Booking')}
                      </button>
                    </div>
                  </div>
                )}

                {/* Step 3: Payment (If necessary) */}
                {bookingStep === 'payment' && (
                  <div className="space-y-6">
                    <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
                      <p className="text-sm font-bold text-gray-500 mb-2">Order Summary</p>
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-[#1e293b] font-medium">{bookingProvider.title}</span>
                        <span className="text-[#1e293b] font-bold">${parseFloat(bookingProvider.price).toFixed(2)}</span>
                      </div>
                      <p className="text-xs text-gray-400">{bookingDate} at {bookingTime}</p>
                    </div>
                    
                    <div className="space-y-4">
                      <p className="text-sm font-bold text-[#1e293b]">Payment Details</p>
                      <div className="relative">
                        <div className="absolute left-4 top-1/2 -translate-y-1/2 flex gap-1">
                          <div className="w-8 h-5 bg-blue-100 rounded"></div>
                          <div className="w-8 h-5 bg-red-100 rounded"></div>
                        </div>
                        <input type="text" placeholder="Card Number" className="w-full pl-24 pr-4 py-3 bg-white border border-gray-200 rounded-xl focus:border-[#c8a97e] outline-none" />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <input type="text" placeholder="MM/YY" className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:border-[#c8a97e] outline-none" />
                        <input type="text" placeholder="CVC" className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:border-[#c8a97e] outline-none" />
                      </div>
                    </div>

                    <div className="flex gap-4 pt-4">
                      <button onClick={() => setBookingStep('details')} className="w-1/3 py-4 bg-gray-100 text-gray-600 rounded-xl font-black uppercase tracking-widest hover:bg-gray-200 transition-all text-xs">Back</button>
                      <button 
                        onClick={handleConfirmBooking}
                        disabled={isProcessing}
                        className="w-2/3 py-4 bg-[#1e293b] text-white rounded-xl font-black uppercase tracking-widest hover:bg-[#0f172a] transition-all flex items-center justify-center gap-2 text-xs"
                      >
                        {isProcessing ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : `Pay $${parseFloat(bookingProvider.price).toFixed(2)} & ${bookingProvider.service_type === 'reservation' ? 'Reserve' : 'Book'}`}
                      </button>
                    </div>
                  </div>
                )}

                {/* Final Step: Success */}
                {bookingStep === 'success' && (
                  <div className="text-center py-8">
                    <div className="w-20 h-20 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-6">
                      <SafeIcon icon={FiCheck} className="w-10 h-10 text-green-500" />
                    </div>
                    <h3 className="text-2xl font-serif text-[#1e293b] mb-2">{bookingProvider.service_type === 'reservation' ? 'Reservation Requested!' : 'Booking Confirmed!'}</h3>
                    <p className="text-gray-500 mb-6">
                      {bookingProvider.service_type === 'reservation' 
                        ? 'Your reservation request has been sent to the merchant and is pending approval.' 
                        : 'Your appointment has been successfully scheduled.'}
                    </p>
                    <div className="bg-gray-50 p-4 rounded-xl border border-gray-100 inline-block text-left mb-6">
                      <p className="text-sm font-bold text-[#1e293b] mb-1">{bookingProvider.title}</p>
                      <p className="text-xs text-gray-500 flex items-center gap-2"><SafeIcon icon={FiCalendar} className="w-3 h-3"/> {bookingDate} at {bookingTime}</p>
                    </div>
                  </div>
                )}

              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}